import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  createNotificationTemplateSchema,
  updateNotificationTemplateSchema,
  type CreateNotificationTemplateDto,
  type NotificationTemplateDetail,
  type UpdateNotificationTemplateDto,
} from '@control-center/shared';

import { cn } from '@control-center/ui';

import { Field, Input, Select, Textarea } from '../../clients/form-primitives';
import { PillButton } from '../../clients/PillButton';
import { extractErrorMessage } from '../../../lib/errors';
import { useCreateTemplate, useUpdateTemplate } from '../../../hooks/use-notification-templates';

export interface TemplateFormProps {
  mode: 'create' | 'edit';
  template?: NotificationTemplateDetail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (result: { id: string; version: number }) => void;
}

interface FormState {
  key: string;
  name: string;
  channel: 'whatsapp' | 'email';
  variantType: '' | 'pf' | 'pj';
  subject: string;
  bodyHtml: string;
  bodyText: string;
  isActive: boolean;
}

const initialFormState: FormState = {
  key: '',
  name: '',
  channel: 'whatsapp',
  variantType: '',
  subject: '',
  bodyHtml: '',
  bodyText: '',
  isActive: true,
};

export function TemplateForm({
  mode,
  template,
  open,
  onOpenChange,
  onSuccess,
}: TemplateFormProps): JSX.Element {
  const [form, setForm] = useState<FormState>(initialFormState);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const createMutation = useCreateTemplate();
  const updateMutation = useUpdateTemplate();
  const isPending = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (!open) return;
    setFieldErrors({});
    if (mode === 'edit' && template) {
      setForm({
        key: template.key,
        name: template.name,
        channel: template.channel === 'email' ? 'email' : 'whatsapp',
        variantType: (template.variantType as '' | 'pf' | 'pj') ?? '',
        subject: template.subject ?? '',
        bodyHtml: template.bodyHtml ?? '',
        bodyText: template.bodyText,
        isActive: template.isActive,
      });
    } else {
      setForm(initialFormState);
    }
    createMutation.reset();
    updateMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, template?.id]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev[key as string]) return prev;
      const { [key as string]: _removed, ...rest } = prev;
      return rest;
    });
  }

  function buildCreateDto(): CreateNotificationTemplateDto {
    return {
      key: form.key.trim(),
      name: form.name.trim(),
      channel: form.channel,
      variant_type: form.variantType ? form.variantType : null,
      subject: form.channel === 'email' ? form.subject.trim() || null : null,
      body_html: form.channel === 'email' ? form.bodyHtml || null : null,
      body_text: form.bodyText,
    };
  }

  function buildUpdateDto(): UpdateNotificationTemplateDto {
    return {
      name: form.name.trim(),
      subject: form.channel === 'email' ? form.subject.trim() || null : null,
      body_html: form.channel === 'email' ? form.bodyHtml || null : null,
      body_text: form.bodyText,
      is_active: form.isActive,
    };
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFieldErrors({});

    if (mode === 'create') {
      const dto = buildCreateDto();
      const parsed = createNotificationTemplateSchema.safeParse(dto);
      if (!parsed.success) {
        const errors: Record<string, string> = {};
        for (const issue of parsed.error.issues) {
          const path = issue.path[0]?.toString() ?? '_';
          errors[path] = issue.message;
        }
        setFieldErrors(errors);
        return;
      }
      try {
        const result = await createMutation.mutateAsync(parsed.data);
        toast.success('Template criado.', { description: `Versão ${result.version}` });
        onSuccess?.({ id: result.id, version: result.version });
        onOpenChange(false);
      } catch (err) {
        toast.error(extractErrorMessage(err, 'Falha ao criar template.'));
      }
      return;
    }

    if (!template) return;
    const dto = buildUpdateDto();
    const parsed = updateNotificationTemplateSchema.safeParse(dto);
    if (!parsed.success) {
      const errors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const path = issue.path[0]?.toString() ?? '_';
        errors[path] = issue.message;
      }
      setFieldErrors(errors);
      return;
    }
    try {
      const result = await updateMutation.mutateAsync({ id: template.id, dto: parsed.data });
      toast.success('Template atualizado.', { description: `Nova versão ${result.version}` });
      onSuccess?.({ id: result.id, version: result.version });
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao atualizar template.'));
    }
  }

  const isEmail = form.channel === 'email';
  const title = mode === 'create' ? 'Novo template' : `Editar — ${template?.key ?? ''}`;
  const versionHint =
    mode === 'edit' && template ? `Salvar criará a versão ${template.version + 1}.` : undefined;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(640px,calc(100vw-32px))] max-h-[90dvh] overflow-y-auto',
            'rounded-[14px] border border-line bg-panel p-6 shadow-lg focus:outline-none',
          )}
        >
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <Dialog.Title
                data-testid="template-form-title"
                className="text-[18px] font-semibold tracking-[-0.005em] text-ink"
              >
                {title}
              </Dialog.Title>
              {versionHint && <p className="mt-1 text-[12px] text-muted">{versionHint}</p>}
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Fechar"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
              >
                <X size={15} strokeWidth={2} />
              </button>
            </Dialog.Close>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
            {mode === 'create' && (
              <Field
                label="Key (snake_case)"
                required
                hint="ex: cobranca_vencida_d3_email_pf"
                error={fieldErrors.key}
              >
                {({ inputId, describedBy }) => (
                  <Input
                    id={inputId}
                    aria-describedby={describedBy}
                    value={form.key}
                    onChange={(e) => setField('key', e.target.value)}
                    placeholder="cobranca_vencida_d3"
                    hasError={Boolean(fieldErrors.key)}
                  />
                )}
              </Field>
            )}

            <Field label="Nome" required error={fieldErrors.name}>
              {({ inputId, describedBy }) => (
                <Input
                  id={inputId}
                  aria-describedby={describedBy}
                  value={form.name}
                  onChange={(e) => setField('name', e.target.value)}
                  placeholder="Cobrança vencida — lembrete D+3"
                  hasError={Boolean(fieldErrors.name)}
                />
              )}
            </Field>

            <div className="grid grid-cols-2 gap-4">
              {mode === 'create' && (
                <Field label="Canal" required error={fieldErrors.channel}>
                  {({ inputId }) => (
                    <Select
                      id={inputId}
                      value={form.channel}
                      onChange={(e) => setField('channel', e.target.value as 'whatsapp' | 'email')}
                    >
                      <option value="whatsapp">WhatsApp</option>
                      <option value="email">Email</option>
                    </Select>
                  )}
                </Field>
              )}

              {mode === 'create' && (
                <Field label="Variante (opcional)" hint="PF, PJ ou universal">
                  {({ inputId }) => (
                    <Select
                      id={inputId}
                      value={form.variantType}
                      onChange={(e) => setField('variantType', e.target.value as '' | 'pf' | 'pj')}
                    >
                      <option value="">Universal</option>
                      <option value="pf">Pessoa física</option>
                      <option value="pj">Pessoa jurídica</option>
                    </Select>
                  )}
                </Field>
              )}
            </div>

            {isEmail && (
              <Field
                label="Assunto"
                required
                hint="Suporta variáveis Mustache {{client.name}}"
                error={fieldErrors.subject}
              >
                {({ inputId, describedBy }) => (
                  <Input
                    id={inputId}
                    aria-describedby={describedBy}
                    value={form.subject}
                    onChange={(e) => setField('subject', e.target.value)}
                    placeholder="Lembrete: cobrança vencida"
                    hasError={Boolean(fieldErrors.subject)}
                  />
                )}
              </Field>
            )}

            {isEmail && (
              <Field
                label="Corpo HTML (opcional)"
                hint="Suporta Mustache. Vazio → usa só body_text."
                error={fieldErrors.body_html}
              >
                {({ inputId, describedBy }) => (
                  <Textarea
                    id={inputId}
                    aria-describedby={describedBy}
                    rows={6}
                    value={form.bodyHtml}
                    onChange={(e) => setField('bodyHtml', e.target.value)}
                    placeholder="<p>Olá {{client.name}}…</p>"
                    hasError={Boolean(fieldErrors.body_html)}
                  />
                )}
              </Field>
            )}

            <Field
              label="Corpo texto"
              required
              hint="Plain text. Para WhatsApp este é o conteúdo enviado."
              error={fieldErrors.body_text}
            >
              {({ inputId, describedBy }) => (
                <Textarea
                  id={inputId}
                  aria-describedby={describedBy}
                  rows={8}
                  maxLength={10_000}
                  value={form.bodyText}
                  onChange={(e) => setField('bodyText', e.target.value)}
                  placeholder="Olá {{client.name}}, sua cobrança…"
                  hasError={Boolean(fieldErrors.body_text)}
                />
              )}
            </Field>

            {mode === 'edit' && (
              <label className="flex items-center gap-2 text-[13px] text-ink">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setField('isActive', e.target.checked)}
                  className="h-4 w-4 rounded border-line bg-panel"
                />
                Ativo
              </label>
            )}

            <div className="mt-2 flex items-center justify-end gap-2">
              <PillButton type="button" onClick={() => onOpenChange(false)}>
                Cancelar
              </PillButton>
              <PillButton type="submit" solid disabled={isPending}>
                {isPending
                  ? mode === 'create'
                    ? 'Criando…'
                    : 'Salvando…'
                  : mode === 'create'
                    ? 'Criar template'
                    : 'Salvar nova versão'}
              </PillButton>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
